export const CUSTOMER_HEADER_MAIN_BG = "#22374a";
export const INLINE_DOCKED_PANEL_WIDTH = 320;
export const INLINE_DOCKED_PANEL_RESERVED_WIDTH = 320;
export const REQUIRED_PREMIUM_PLAN_LEVEL = 3;
export const BOT_ROOT_SELECTOR = "[data-planybot-root='true']";
export const MESSAGE_LINK_RE = /(https?:\/\/[^\s)]+|\/[a-zA-Z][a-zA-Z0-9/_-]*)/g;

export const STARTER_PROMPTS = [
    "Show today's birthdays",
    "Show expired docs",
    "Show my appointments today",
    "Show past due medication",
    "Tell me about this page",
    "What can I do here?",
    "Create a simple booking type called Follow-up 30 min",
    "Find nearby adult family homes with availability",
    "Help me book a visit this week",
    "Show me how to add a resident medication",
];

export const PLAN_DISPLAY_ORDER = ["FREE", "STANDARD", "PREMIUM", "ENTERPRISE"];

export const FEATURE_SECTION_LABELS = {
    resident: "Resident & Care Management",
    staffing: "Staffing & Care Provider",
    referrals: "Referrals & Visibility",
    booking: "Scheduling & Booking",
    team: "Team Scheduling",
    admin: "Admin Controls",
    analytics: "Analytics & Reporting",
    operations: "Operations",
    compliance: "Compliance",
    integrations: "Integrations",
    automation: "Automation & Reminders",
    notifications: "Notifications",
    ai: "AI Assistance",
};

export const FEATURE_TEXT_OVERRIDES = {
    "Medication & MAR management": "eMAR and medication management",
    "Vitals & daily care notes": "Vitals, progress notes, and daily care logs",
    "Custom booking page": "Branded booking page",
    "Email reminders": "Automated reminder emails",
    "Staff hiring & job posting": "Staff hiring and job posting",
    "Applicant tracking": "Applicant pipeline tracking",
    "Candidate communication": "Candidate messaging and follow-up",
    "Everything in Standard": "Everything in Standard Plan",
    "Business, resident, and staff document management": "Business, resident, and staff document upload with expiry tracking",
    "Role-based access control (assign staff roles)": "Role-based access control for staff assignment and access",
    "Custom role builder": "Custom role and permission builder",
    "AI workflow assistance": "AI workflow assistance(PlanyCare Assistant)",
    "Advanced compliance workflows": "Step-by-step compliance tasks with clear status tracking",
    "Audit-ready reporting": "Inspection-ready reports with timestamps and staff action history",
    "Medication OCR intake (images/PDF to drafts)": "Scan medication documents into medication entries",
    "Medication OCR intake": "Scan medication documents into medication entries",
    "Multiple businesses and locations when each business has its own subscription": "Multiple businesses and locations when each business has its own paid subscription",
    "See staff availability across your subscribed businesses": "See staff availability across your subscribed businesses",
    "Team scheduling across shared staff calendars": "Team scheduling across shared staff calendars",
    "Shared admin controls": "Shared admin controls across businesses",
    "Centralized booking management across businesses": "Centralized booking management across businesses",
    "Advanced host and scheduler permissions": "Advanced host and scheduler permissions",
    "Booking analytics and reporting": "Booking analytics and reporting",
};

export const BOT_SYSTEM_PROMPT = `You are PlanyBot, an assistant for Plany.
- Help users find features, pages, and next steps inside the app.
- Always prioritize the CURRENT page and active form context.
- If the user says "this form", "this page", or "here", answer using provided page context first.
- For complex questions, give a clear workflow with numbered steps and where to click.
- Use only capabilities visible in provided context. Never guess hidden features.
- If needed context is missing, say what is missing and ask one clarifying question.
- Give concise, actionable responses with short steps.
- Never invent product capabilities. If context is missing, say so and ask a clarifying question.
- If asked for things outside Plany, still help briefly.
- Do not claim actions were completed unless user confirms.`;

export const ROUTE_CONTEXTS = [
    {
        matches: /^\/biz\/business(?:\/|$)/,
        hint: "Business settings tabs for profile, hours, images, payment, and subscription.",
        summary: "the Business settings page",
        tasks: [
            "update business profile settings",
            "manage business hours and images",
            "configure payment integration",
            "review subscription and billing",
        ],
    },
    {
        matches: /^\/biz\/pricing(?:\/|$)/,
        hint: "Pricing and plan management page.",
        summary: "the Pricing and subscription page",
        tasks: [
            "compare plans",
            "switch plan",
            "adjust resident seats",
            "review subscription status",
        ],
    },
    {
        matches: /^\/biz\/profile(?:\/|$)/,
        hint: "Business profile/settings page.",
        summary: "the Business Profile settings page",
        tasks: [
            "update general business information",
            "set timezone and core settings",
            "change password",
            "choose default start page",
        ],
    },
    {
        matches: /^\/biz\/bookingtypes(?:\/|$)/,
        hint: "Booking types home page.",
        summary: "the Booking Types management page",
        tasks: [
            "review existing booking types",
            "open a booking type for editing",
            "start creating a new booking type",
        ],
    },
    {
        matches: /^\/biz\/create(?:\/|$)|^\/biz\/update(?:\/|$)/,
        hint: "Booking type setup form (basic info, timing, availability, summary).",
        summary: "the Booking Type setup form",
        tasks: [
            "set booking basics",
            "configure duration and time rules",
            "set host availability",
            "review summary before saving",
        ],
    },
    {
        matches: /^\/biz\/calendar(?:\/|$)|^\/myschedule(?:\/|$)/,
        hint: "Business calendar and schedule management page.",
        summary: "the Calendar and schedule page",
        tasks: [
            "view upcoming bookings",
            "check staff schedules",
            "manage calendar availability",
        ],
    },
    {
        matches: /^\/biz\/printview(?:\/|$)/,
        hint: "Printable schedule/portal view.",
        summary: "the Print view page",
        tasks: [
            "preview printable booking schedules",
            "review booking details in print format",
        ],
    },
    {
        matches: /^\/biz\/staff(?:\/|$)/,
        hint: "Staff management page.",
        summary: "the Staff management page",
        tasks: [
            "manage staff profiles",
            "review staff availability",
            "assign booking-related responsibilities",
        ],
    },
    {
        matches: /^\/biz\/integrations(?:\/|$)/,
        hint: "Integrations setup and status page.",
        summary: "the Integrations page",
        tasks: [
            "review available integrations",
            "configure integration settings",
            "check integration connection status",
        ],
    },
    {
        matches: /^\/biz\/dashboard(?:\/|$)/,
        hint: "Care provider dashboard.",
        summary: "the Provider dashboard",
        tasks: [
            "review referral and request workflow",
            "monitor dashboard activity and follow-ups",
        ],
    },
    {
        matches: /^\/biz\/resident(?:\/|$)/,
        hint: "Resident management forms and records.",
        summary: "the Resident management area",
        tasks: [
            "manage resident profiles",
            "review care and medication records",
            "update resident-related data",
        ],
    },
    {
        matches: /^\/biz\/subscription-success(?:\/|$)|^\/billing\/success(?:\/|$)|^\/billing\/cancel(?:\/|$)/,
        hint: "Subscription billing result page.",
        summary: "the Billing result page",
        tasks: [
            "review billing outcome",
            "return to pricing or business settings",
        ],
    },
    {
        matches: /^\/care\/request(?:\/|$)|^\/b\/[^/]+\/care\/referral(?:\/|$)/,
        hint: "Care request/referral intake page.",
        summary: "the Referral request page",
        tasks: [
            "submit or review referral request details",
            "provide care placement information",
        ],
    },
    {
        matches: /^\/care\/concierge(?:\/|$)|^\/care\/claim-resume(?:\/|$)|^\/care\/agreement(?:\/|$)/,
        hint: "Care onboarding and concierge workflow pages.",
        summary: "a Care onboarding workflow page",
        tasks: [
            "complete provider onboarding steps",
            "review agreement or concierge requirements",
        ],
    },
    {
        matches: /^\/care\/jobs(?:\/|$)|^\/care\/myjobs(?:\/|$)|^\/b\/[^/]+\/jobs(?:\/|$)/,
        hint: "Care jobs board/detail/manage pages.",
        summary: "the Jobs page",
        tasks: [
            "browse job postings",
            "review job details",
            "manage your job postings or applications",
        ],
    },
    {
        matches: /^\/care\/city(?:\/|$)|^\/care\/state(?:\/|$)|^\/care(?:\/|$)|^\/category\/(?:\/|$)|^\/search(?:\/|$)/,
        hint: "Customer/care marketplace discovery pages.",
        summary: "a care discovery page",
        tasks: [
            "browse providers",
            "filter by location/category",
            "open provider pages",
        ],
    },
    {
        matches: /^\/referrer\/portal(?:\/|$)|^\/care\/referrals\/group(?:\/|$)/,
        hint: "Referrer portal and referral group pages.",
        summary: "the Referrer portal page",
        tasks: [
            "track referral updates",
            "review referral conversations",
        ],
    },
    {
        matches: /^\/book\/embed(?:\/|$)|^\/embed\/(?:\/|$)|^\/b\/[^/]+\/booking\/(?:\/|$)/,
        hint: "Public booking and embedded booking pages.",
        summary: "the Public booking page",
        tasks: [
            "pick service/booking type",
            "select date and time",
            "confirm booking details",
        ],
    },
    {
        matches: /^\/b\/[^/]+\/[^/]+\/wallet(?:\/|$)|^\/b\/wallet(?:\/|$)|^\/coins\/buy(?:\/|$)/,
        hint: "Wallet and coin purchase pages.",
        summary: "the Wallet page",
        tasks: [
            "review balance and transactions",
            "buy coins",
            "manage wallet actions",
        ],
    },
    {
        matches: /^\/b\/[^/]+\/[^/]+\/tip(?:\/|$)|^\/b\/[^/]+\/tip(?:\/|$)/,
        hint: "Tip page.",
        summary: "the Tip page",
        tasks: [
            "submit a tip",
            "review tip details before confirming",
        ],
    },
    {
        matches: /^\/login(?:\/|$)|^\/signup(?:\/|$)|^\/start-free-trial(?:\/|$)|^\/forgotpassword(?:\/|$)/,
        hint: "Authentication page.",
        summary: "an authentication page",
        tasks: [
            "sign in or create an account",
            "recover account access",
        ],
    },
    {
        matches: /^\/about(?:\/|$)|^\/privacy(?:\/|$)|^\/terms(?:\/|$)|^\/faq(?:\/|$)/,
        hint: "Informational/legal page.",
        summary: "an informational page",
        tasks: [
            "review policy or help information",
        ],
    },
    {
        matches: /^\/profile(?:\/|$)/,
        hint: "User profile page.",
        summary: "the User profile page",
        tasks: [
            "update your account profile settings",
            "review account preferences",
        ],
    },
    {
        matches: /^\/biz\/approve(?:\/|$)/,
        hint: "Staff approval/invitation response page.",
        summary: "the Staff approval page",
        tasks: [
            "review invitation details",
            "approve or process staff status update",
        ],
    },
    {
        matches: /^\/b\/[^/]+(?:\/|$)/,
        hint: "Public business page path under canonical /b/:businessUrl routes.",
        summary: "a public business page",
        tasks: [
            "browse public business details",
            "open booking, staff, or care-related public pages",
        ],
    },
];

export const RESIDENT_TAB_CONTEXTS = [
    {
        match: /Resident Face Sheet/i,
        summary: "the Resident Face Sheet tab",
        tasks: [
            "review demographics and core resident profile details",
            "update face sheet and contact/admission information",
        ],
    },
    {
        match: /Care Plan/i,
        summary: "the Care Plan tab",
        tasks: [
            "maintain diagnoses, risks, and mitigation strategy",
            "update mobility/cognitive/code status and related plan notes",
        ],
    },
    {
        match: /Daily Notes/i,
        summary: "the Daily Notes tab",
        tasks: [
            "add shift or daily care notes",
            "review note history for this resident",
        ],
    },
    {
        match: /Medication\s*&\s*MAR/i,
        summary: "the Medication & MAR tab",
        tasks: [
            "add and edit medications",
            "record daily MAR entries",
            "review monthly MAR and print MAR reports",
        ],
    },
    {
        match: /Vitals/i,
        summary: "the Vitals tab",
        tasks: [
            "record vitals entries",
            "review vitals history and trend charts",
        ],
    },
    {
        match: /Alerts/i,
        summary: "the Alerts tab",
        tasks: [
            "review active resident alerts",
            "resolve or follow up on alert items",
        ],
    },
    {
        match: /Incident Report/i,
        summary: "the Incident Report tab",
        tasks: [
            "create and review incident reports",
            "track incident details and documentation",
        ],
    },
    {
        match: /Documents/i,
        summary: "the Documents tab",
        tasks: [
            "upload and manage resident documents",
            "review document records and files",
        ],
    },
    {
        match: /Image/i,
        summary: "the Resident Image tab",
        tasks: [
            "upload or update resident image/photo",
            "manage profile image display",
        ],
    },
];

export const BUSINESS_SETTINGS_TAB_CONTEXTS = [
    {
        match: /Business Basic/i,
        summary: "the Business Basic tab",
        tasks: [
            "edit business name, contact details, and core profile info",
            "update public profile basics for your listing and booking pages",
        ],
    },
    {
        match: /Business Hour/i,
        summary: "the Business Hour tab",
        tasks: [
            "set working hours by weekday",
            "update timezone-aware availability windows",
        ],
    },
    {
        match: /Images/i,
        summary: "the Business Images tab",
        tasks: [
            "upload and manage business photos",
            "control image order and visual profile quality",
        ],
    },
    {
        match: /Payment Integration/i,
        summary: "the Payment Integration tab",
        tasks: [
            "connect payment provider settings",
            "review payment setup for booking and billing flows",
        ],
    },
    {
        match: /Documents/i,
        summary: "the Business Documents tab",
        tasks: [
            "upload and organize business-level compliance and legal documents",
            "set document expiration dates and monitor upcoming renewals",
        ],
    },
    {
        match: /Subscription\s*&\s*Billing/i,
        summary: "the Subscription and Billing tab",
        tasks: [
            "review current plan and resident seats",
            "switch plans through Stripe checkout",
        ],
    },
];

export const STAFF_TAB_CONTEXTS = [
    {
        match: /Profile/i,
        summary: "the Staff Profile tab",
        tasks: [
            "update staff contact and profile details",
            "review role-specific profile settings",
        ],
    },
    {
        match: /Working Hour/i,
        summary: "the Staff Working Hour tab",
        tasks: [
            "set weekly availability for this staff member",
            "control appointment windows and follow-up schedule readiness",
        ],
    },
    {
        match: /Assigned Booking Types/i,
        summary: "the Assigned Booking Types tab",
        tasks: [
            "assign which booking types the staff member can host",
            "control service ownership and booking routing",
        ],
    },
    {
        match: /Integration/i,
        summary: "the Staff Integration tab",
        tasks: [
            "connect calendar and external integration items",
            "resolve synchronization setup issues",
        ],
    },
    {
        match: /Document/i,
        summary: "the Staff Document tab",
        tasks: [
            "upload, review, and organize staff documents",
            "track required compliance file coverage",
        ],
    },
    {
        match: /Image/i,
        summary: "the Staff Image tab",
        tasks: [
            "upload or update staff profile image",
            "manage visibility-ready profile picture",
        ],
    },
];

export const ROUTE_TAB_CONTEXT_GROUPS = [
    { path: /^\/biz\/resident(?:\/|$)/, tabs: RESIDENT_TAB_CONTEXTS },
    { path: /^\/biz\/business(?:\/|$)/, tabs: BUSINESS_SETTINGS_TAB_CONTEXTS },
    { path: /^\/biz\/staff(?:\/|$)/, tabs: STAFF_TAB_CONTEXTS },
];

export const ROUTE_ENRICHMENTS = [
    {
        matches: /^\/biz\/dashboard(?:\/|$)/,
        forms: ["Referral pipeline cards", "Request intake actions", "Follow-up status area"],
        workflows: [
            {
                name: "Work referral pipeline",
                match: /referral|request|pipeline|follow[- ]?up|dashboard/i,
                steps: [
                    "Review pending or new referral/request cards first.",
                    "Open the selected item and verify required details.",
                    "Update status and send follow-up communication.",
                    "Return to dashboard and confirm the item moved to the correct stage.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/business(?:\/|$)/,
        tabs: [
            "Business Basic",
            "Business Hour",
            "Images",
            "Payment Integration",
            "Documents",
            "Subscription & Billing",
        ],
        forms: [
            "Business profile form",
            "Working hours editor",
            "Image manager",
            "Payment integration setup",
            "Business document manager",
            "Plan and billing controls",
        ],
        workflows: [
            {
                name: "Update business profile and hours",
                match: /business|profile|hours|timezone|address|phone|name/i,
                steps: [
                    "Open Business Basic and update core business details.",
                    "Open Business Hour and set weekly availability by day.",
                    "Save changes and re-open the section to confirm values persisted.",
                ],
            },
            {
                name: "Set up branding images",
                match: /image|photo|logo|picture|upload/i,
                steps: [
                    "Open the Images tab in business settings.",
                    "Upload new images and remove low-quality or outdated images.",
                    "Confirm final image order and preview public-facing profile.",
                ],
            },
            {
                name: "Manage business documents",
                match: /document|license|insurance|policy|compliance|expiry|expiration/i,
                steps: [
                    "Open the Documents tab in business settings.",
                    "Select the document type and upload the file.",
                    "Set expiration date when applicable so reminders can track renewals.",
                    "Review grouped document list and remove outdated files.",
                ],
            },
            {
                name: "Manage subscription from business settings",
                match: /subscription|billing|stripe|plan|seat/i,
                steps: [
                    "Open Subscription & Billing and review current plan and seat usage.",
                    "Choose a target plan if you need to switch tiers.",
                    "Run Stripe checkout and verify billing success state after return.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/pricing(?:\/|$)/,
        tabs: ["Free", "Standard", "Premium", "Resident seats"],
        forms: ["Plan cards", "Resident seat stepper", "Stripe checkout redirect"],
        workflows: [
            {
                name: "Switch to another plan",
                match: /switch|upgrade|downgrade|change.*plan|subscribe|premium|standard|free/i,
                steps: [
                    "Confirm which plan is currently marked as Current Plan.",
                    "Set resident seats to the desired number before checkout.",
                    "Click Subscribe on the target plan.",
                    "Complete Stripe checkout and verify success page/state.",
                ],
            },
            {
                name: "Adjust resident seat count",
                match: /seat|resident.*count|limit|quantity/i,
                steps: [
                    "Use the plus/minus controls in the Resident seats panel.",
                    "Review purchased, used, and remaining seats summary.",
                    "Click Update Resident Limit to apply seat changes.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/profile(?:\/|$)|^\/profile(?:\/|$)/,
        forms: [
            "General account settings",
            "Timezone and preference settings",
            "Password update form",
            "Default start page setting",
        ],
        workflows: [
            {
                name: "Update account settings",
                match: /profile|settings|timezone|password|default.*page|start page/i,
                steps: [
                    "Open the relevant profile settings section.",
                    "Update the required fields (general info, timezone, or password).",
                    "Save and confirm the updated values are shown after refresh.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/bookingtypes(?:\/|$)/,
        forms: ["Booking types list", "Create new booking type entry", "Edit existing booking type entry"],
        workflows: [
            {
                name: "Create a booking type",
                match: /create|new.*booking type|service|appointment type/i,
                steps: [
                    "Open booking types and choose Create.",
                    "Complete booking basics and scheduling settings.",
                    "Assign hosts, set visibility, and save.",
                    "Validate in booking type list and public booking page preview.",
                ],
            },
            {
                name: "Edit or troubleshoot booking type",
                match: /edit|update|availability|duration|visibility|host|follow[- ]?up/i,
                steps: [
                    "Open the target booking type from the list.",
                    "Update the section (Basics, Scheduling, Host, or Visibility).",
                    "Save and validate summary/preview reflects changes.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/create(?:\/|$)|^\/biz\/update(?:\/|$)/,
        tabs: [
            "Booking Basics",
            "Scheduling Settings",
            "Hosts and Availability",
            "Booking Visibility",
            "More Options",
            "Custom Questions",
            "Summary",
        ],
        forms: [
            "Booking type basic info",
            "Date range and recurrence",
            "Host assignment and availability mode",
            "Visibility and booking URL options",
            "Payment and meeting options",
            "Invitee custom questions",
        ],
        workflows: [
            {
                name: "Complete booking type setup end-to-end",
                match: /setup|create|configure|complete|publish|booking type/i,
                steps: [
                    "Complete Booking Basics (title, duration, location, description).",
                    "Configure Scheduling Settings (date range, recurrence, buffers).",
                    "Assign Hosts and set availability behavior.",
                    "Set Booking Visibility, then finish More Options and Custom Questions.",
                    "Review Booking Summary and save.",
                ],
            },
            {
                name: "Configure custom host availability",
                match: /custom.*availability|availability|host|quick|schedule/i,
                steps: [
                    "Open Hosts and Availability in the booking type editor.",
                    "Select hosts and switch to custom availability mode if needed.",
                    "Set weekly windows and verify they align with working hours.",
                    "Save and confirm summary reflects custom selection.",
                ],
            },
            {
                name: "Set booking visibility and sharing",
                match: /visibility|private|public|booking page|share|url/i,
                steps: [
                    "Open Booking Visibility section.",
                    "Set visible-to-staff and public booking page display options.",
                    "Save and copy booking URL from More Options when needed.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/calendar(?:\/|$)|^\/myschedule(?:\/|$)/,
        forms: ["Calendar grid", "Booking detail panel", "Schedule filters"],
        workflows: [
            {
                name: "Review and manage schedule",
                match: /calendar|schedule|appointment|follow[- ]?up|availability/i,
                steps: [
                    "Open calendar view and apply date/staff filters.",
                    "Open booking details for target appointments.",
                    "Adjust follow-up or availability actions and validate on calendar.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/staff(?:\/|$)/,
        tabs: ["Profile", "Working Hour", "Assigned Booking Types", "Integration", "Document", "Image"],
        forms: [
            "Staff profile editor",
            "Working hours form",
            "Assigned booking type controls",
            "Integration setup",
            "Staff document manager",
            "Staff image uploader",
        ],
        workflows: [
            {
                name: "Onboard or update staff profile",
                match: /staff|profile|onboard|edit staff|provider/i,
                steps: [
                    "Open staff member Profile tab and update core details.",
                    "Set Working Hour for availability windows.",
                    "Upload required documents and image for readiness.",
                ],
            },
            {
                name: "Assign services to staff",
                match: /assign|booking type|service|follow[- ]?up|host/i,
                steps: [
                    "Open Assigned Booking Types tab for the selected staff member.",
                    "Enable booking types that this staff can host.",
                    "Validate assignment in booking type host configuration.",
                ],
            },
        ],
    },
    {
        matches: /^\/biz\/resident(?:\/|$)/,
        tabs: [
            "Resident Face Sheet",
            "Care Plan",
            "Daily Notes",
            "Medication & MAR",
            "Vitals",
            "Alerts",
            "Incident Report",
            "Documents",
            "Image",
        ],
        forms: [
            "Resident demographic and admission profile",
            "Care plan and risk fields",
            "Daily note entry",
            "Medication and MAR forms",
            "Vitals log form",
            "Incident report form",
            "Resident document upload form",
        ],
        workflows: [
            {
                name: "Complete resident intake and profile",
                match: /intake|new resident|face sheet|admission|demographic/i,
                steps: [
                    "Open Resident Face Sheet and complete required identity/admission fields.",
                    "Upload initial resident documents and image.",
                    "Open Care Plan and set baseline diagnoses and risk items.",
                ],
            },
            {
                name: "Manage medication and MAR",
                match: /medication|mar|dose|administer|ocr/i,
                steps: [
                    "Open Medication & MAR tab for the selected resident.",
                    "Create or update medication entries and schedule details.",
                    "Record MAR administration entries and review monthly MAR output.",
                ],
            },
            {
                name: "Track clinical updates and incidents",
                match: /vitals|daily notes|alert|incident|document expiry|reminder/i,
                steps: [
                    "Use Daily Notes for shift/day observations.",
                    "Record vitals in Vitals and monitor trends.",
                    "Handle Alerts and Incident Report for urgent/compliance events.",
                    "Use Documents tab to keep supporting records complete.",
                ],
            },
        ],
    },
    {
        matches: /^\/book\/embed(?:\/|$)|^\/embed\/(?:\/|$)|^\/b\/[^/]+\/booking\/(?:\/|$)/,
        forms: ["Booking type selection", "Date/time picker", "Booking confirmation form"],
        workflows: [
            {
                name: "Complete public booking",
                match: /book|appointment|time|reschedule|confirm/i,
                steps: [
                    "Select booking type or service.",
                    "Pick available date and time slot.",
                    "Fill required invitee fields and confirm booking.",
                ],
            },
        ],
    },
];

